import React from 'react';
import { Divider } from '@mui/material';
import { fmtPrice, fmtPercent } from '@upstart/harmony/lib/formatter';
import { BaseLendingTileParams, BaseLendingTile } from '../BaseLendingTile';
import { LendingStatusBadgeEnum, LendingValueBadgeEnum } from '../types';

import { LendingStatusBadge, LendingValueBadge } from '../LendingBadge';
import messages from '../messages.json';
import styles from './LendingOfferTile.module.scss';

export type LendingOfferTileProps = Pick<
  BaseLendingTileParams,
  | 'financeSourceName'
  | 'financeImageUrl'
  | 'onClick'
  | 'isSelected'
  | 'checked'
  | 'onCheckboxToggle'
  | 'checkboxDisabled'
  | 'height'
> & {
  monthlyPayment?: number;
  statusBadge?: LendingStatusBadgeEnum | null;
  valueBadge?: LendingValueBadgeEnum | null;
  apr?: number;
  term?: number;
  downPayment?: number;
};

const PAYMENT_LABEL = messages.lendingOfferTile.month;
const APR_LABEL = messages.lendingOfferTile.apr;
const TERM_LABEL = messages.lendingOfferTile.months;
const DOWN_PAYMENT_LABEL = messages.lendingOfferTile.down;
const NA = messages.lendingOfferTile.na;

const formatDownPayment = (downPayment?: number): string => {
  if (downPayment === undefined) {
    return messages.lendingOfferTile.na;
  }
  const downPaymentString = fmtPrice(downPayment);
  if (downPayment === 0 || Math.round(downPayment) === downPayment) {
    return downPaymentString.split('.')[0];
  }
  return downPaymentString;
};

export function LendingOfferTile({
  financeSourceName,
  financeImageUrl,
  statusBadge,
  valueBadge,
  monthlyPayment,
  apr,
  term,
  checked,
  onCheckboxToggle,
  checkboxDisabled,
  onClick,
  isSelected,
  height,
  downPayment,
}: LendingOfferTileProps) {
  const statusBadgeElement = statusBadge ? (
    <LendingStatusBadge statusBadge={statusBadge} />
  ) : null;
  const valueBadgeElement = valueBadge ? (
    <LendingValueBadge valueBadge={valueBadge} />
  ) : null;

  const monthlyPaymentStr = monthlyPayment
    ? fmtPrice(monthlyPayment)
    : messages.lendingOfferTile.na;
  const downPaymentStr = formatDownPayment(downPayment);

  const aprStr = apr ? fmtPercent(apr) : NA;
  const termStr = term ? term.toString() : NA;

  const body = (
    <div
      className={styles.lendingOfferBody}
      data-testid="lending-offer-tile-body"
    >
      <div
        className={styles.monthlyPayment}
        data-testid="monthly-payment-section"
      >
        <div className={styles.rowOne}>{monthlyPaymentStr}</div>
        <div className={styles.rowTwo}>{PAYMENT_LABEL}</div>
      </div>
      <Divider className={styles.divider} orientation="vertical" flexItem />
      <div className={styles.apr} data-testid="apr-section">
        <div className={styles.rowOne}>{aprStr}</div>
        <div className={styles.rowTwo}>{APR_LABEL}</div>
      </div>
      <Divider className={styles.divider} orientation="vertical" flexItem />
      <div className={styles.term} data-testid="term-section">
        <div className={styles.rowOne}>{termStr}</div>
        <div className={styles.rowTwo}>{TERM_LABEL}</div>
      </div>
      <Divider className={styles.divider} orientation="vertical" flexItem />
      <div className={styles.down} data-testid="downPayment-section">
        <div className={styles.rowOne}>{downPaymentStr}</div>
        <div className={styles.rowTwo}>{DOWN_PAYMENT_LABEL}</div>
      </div>
    </div>
  );

  return (
    <BaseLendingTile
      statusBadge={statusBadgeElement}
      valueBadge={valueBadgeElement}
      lendingValueBadge={valueBadge}
      body={body}
      financeSourceName={financeSourceName}
      financeImageUrl={financeImageUrl}
      checked={checked}
      onCheckboxToggle={onCheckboxToggle}
      checkboxDisabled={checkboxDisabled}
      onClick={onClick}
      isSelected={isSelected}
      height={height}
    />
  );
}
